import { useTranslation } from 'react-i18next'

import { Dialog, DialogContent, DialogTitle } from '#/shared/ui/dialog'
import { CloseIcon } from '#/shared/ui/icons'

import { CARRIER_REQUEST_WAITING_STATUSES } from '../model/constants'
import type {
  CarrierRequestItem,
  CarrierRequestPending,
  CarrierRequestsHandlers,
  CarrierRequestsOrderSummary,
} from '../model/types'
import { OrderSummaryCard } from './order-summary-card'
import { CarrierRequestsListSkeleton } from './request-row-skeleton'
import { RequestSection } from './request-section'

interface CarrierRequestsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order?: CarrierRequestsOrderSummary
  requests: CarrierRequestItem[]
  totalDispatchedByYou?: number
  isLoading?: boolean
  pending?: CarrierRequestPending | null
  handlers?: CarrierRequestsHandlers
}

export function CarrierRequestsDialog({
  open,
  onOpenChange,
  order,
  requests,
  totalDispatchedByYou,
  isLoading,
  pending,
  handlers = {},
}: CarrierRequestsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <CarrierRequestsDialogBody
          onClose={() => onOpenChange(false)}
          order={order}
          requests={requests}
          totalDispatchedByYou={totalDispatchedByYou}
          isLoading={isLoading}
          pending={pending}
          handlers={handlers}
        />
      )}
    </Dialog>
  )
}

function CarrierRequestsDialogBody({
  onClose,
  order,
  requests,
  totalDispatchedByYou,
  isLoading,
  pending,
  handlers,
}: {
  onClose: () => void
  order?: CarrierRequestsOrderSummary
  requests: CarrierRequestItem[]
  totalDispatchedByYou?: number
  isLoading?: boolean
  pending?: CarrierRequestPending | null
  handlers: CarrierRequestsHandlers
}) {
  const waiting = requests.filter((r) =>
    CARRIER_REQUEST_WAITING_STATUSES.includes(r.status),
  )
  const responded = requests.filter(
    (r) => !CARRIER_REQUEST_WAITING_STATUSES.includes(r.status),
  )
  return (
    <DialogContent className="max-w-235">
      <DialogHeaderRow onClose={onClose} />
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {order && <OrderSummaryCard order={order} />}
        {isLoading ? (
          <CarrierRequestsListSkeleton />
        ) : (
          <>
            {waiting.length > 0 && (
              <RequestSection
                titleKey="carrierRequests.requestsWaiting"
                count={waiting.length}
                requests={waiting}
                distanceMiles={order?.distanceMiles}
                handlers={handlers}
                pending={pending}
                totalDispatchedByYou={totalDispatchedByYou}
              />
            )}
            {responded.length > 0 && (
              <RequestSection
                titleKey="carrierRequests.respondedRequests"
                count={responded.length}
                requests={responded}
                distanceMiles={order?.distanceMiles}
                handlers={handlers}
                pending={pending}
              />
            )}
            {requests.length === 0 && <EmptyState />}
          </>
        )}
      </div>
    </DialogContent>
  )
}

function DialogHeaderRow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <header className="flex items-center justify-between gap-2.5 border-b border-gray-200 bg-white px-5 py-2.5">
      <DialogTitle className="text-base font-semibold text-gray-500">
        {t('carrierRequests.title')}
      </DialogTitle>
      <button
        type="button"
        aria-label={t('carrierRequests.close')}
        onClick={onClose}
        className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
      >
        <CloseIcon className="size-3.5" />
      </button>
    </header>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 items-center justify-center bg-warm-25 p-8">
      <p className="text-sm text-gray-500">{t('carrierRequests.empty')}</p>
    </div>
  )
}
